import type { ActionBucket, RenewalFamily } from "@/types/renewals";

// Each filter keeps the values picked in its dropdown; an empty list leaves the filter off.
// A family matches a filter when it has any of the picked values.
export interface RenewalFilters {
  search: string;
  categoryL2: string[];
  categoryL3: string[];
  ownerId: string[];
  termination: string[];
  actionDue: ActionBucket[];
  status: string[];
}

export type RenewalSort = "notice" | "notice_desc" | "term_end" | "value" | "supplier";

export const EMPTY_RENEWAL_FILTERS: RenewalFilters = {
  search: "",
  categoryL2: [],
  categoryL3: [],
  ownerId: [],
  termination: [],
  actionDue: [],
  status: [],
};

const matchesSearch = (family: RenewalFamily, term: string) =>
  [family.supplier_name, family.family_name, family.contract_title].some(value =>
    value?.toLocaleLowerCase().includes(term),
  );

const matchesAny = <T extends string>(selected: T[], value: T | null) =>
  selected.length === 0 || (value !== null && selected.includes(value));

export const filterRenewalFamilies = (families: RenewalFamily[], filters: RenewalFilters) => {
  const term = filters.search.trim().toLocaleLowerCase();
  return families.filter(
    family =>
      (!term || matchesSearch(family, term)) &&
      matchesAny(filters.categoryL2, family.category_l2) &&
      matchesAny(filters.categoryL3, family.category_l3) &&
      matchesAny(filters.ownerId, family.owner_user_id) &&
      matchesAny(filters.termination, family.termination_type ?? "unknown") &&
      matchesAny(filters.status, family.procurement_status) &&
      matchesAny(filters.actionDue, family.action_bucket),
  );
};

// ISO dates compare as strings; a missing date sorts last.
const compareDates = (left: string | null, right: string | null) => {
  if (left === right) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return left < right ? -1 : 1;
};

const compareValueDesc = (left: string | null, right: string | null) =>
  (right === null ? -Infinity : Number(right)) - (left === null ? -Infinity : Number(left));

const bySupplier = (left: RenewalFamily, right: RenewalFamily) =>
  left.supplier_name.localeCompare(right.supplier_name, undefined, { sensitivity: "base" }) ||
  left.family_name.localeCompare(right.family_name, undefined, { sensitivity: "base" }) ||
  left.relation_id.localeCompare(right.relation_id);

const COMPARATORS: Record<RenewalSort, (left: RenewalFamily, right: RenewalFamily) => number> = {
  notice: (left, right) => compareDates(left.action_date, right.action_date) || bySupplier(left, right),
  // Latest action date first; families without a date still come last.
  notice_desc: (left, right) =>
    (left.action_date === null || right.action_date === null
      ? compareDates(left.action_date, right.action_date)
      : compareDates(right.action_date, left.action_date)) || bySupplier(left, right),
  term_end: (left, right) => compareDates(left.term_end_date, right.term_end_date) || bySupplier(left, right),
  value: (left, right) =>
    compareValueDesc(left.total_contract_value_gbp, right.total_contract_value_gbp) || bySupplier(left, right),
  supplier: bySupplier,
};

export const sortRenewalFamilies = (families: RenewalFamily[], sort: RenewalSort) =>
  [...families].sort(COMPARATORS[sort]);
